import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { Select } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { usePopoverContext } from "@msuite/ui/popover";
import { toast } from "@msuite/ui/toast";
import { responseHandler, scrollIntoView } from "@msuite/ui/utils";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { usePartnerships } from "@/hooks/use-partnerships";

/** Props Interface */
interface AddCrewPopoverProps {
	planId: string;
}

export function AddCrewPopover({ planId }: AddCrewPopoverProps) {
	/** Context */
	const { handleOnClose } = usePopoverContext();

	/** Hooks */
	const { organization } = useAuth();
	const { partnerships, isFetching, refetch } = usePartnerships("host", ["active"]);

	const methods = useForm<{
		organizationId: string | null;
	}>({
		defaultValues: {
			organizationId: organization?.id ?? null,
		},
	});

	const { t } = useTranslation();

	/** Functions */
	async function handleOnSubmit() {
		try {
			if (!planId) throw new Error("No plan id");
			const orgId = methods.getValues("organizationId");
			if (!orgId) throw new Error("No organization id");
			const response = await client.plan[":planId"].crew.$post({
				param: { planId },
				json: { orgId },
			});
			const data = await responseHandler(response);
			scrollIntoView({ id: `crew-${data.crew?.id}` });
			handleOnClose();
		} catch (error) {
			toast.error(t("general.something_went_wrong"));
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<form
				className="w-75 v-stack"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<p>{t("planning.crews.add_crew")}</p>
				<Divider className="card-divider" />
				<div className="gap-4 mb-1 v-stack">
					<div className="h-stack items-end">
						<Select
							label={t("organization.company")}
							control={methods.control}
							autoFocus
							name="organizationId"
							isDisabled={isFetching}
							isRequired
							isNullable={false}
							options={[
								{
									value: organization?.id ?? "#",
									label: t("planning.crews.own_crew"),
									item: {},
								},
							].concat(
								partnerships?.map((partnership) => ({
									value: partnership.partnerOrgId,
									label: partnership._partner?.name,
									item: {},
								})) ?? [],
							)}
							itemNotFoundLabel={undefined}
						/>
						<Button
							isLoading={isFetching}
							size="sm"
							onClick={refetch}
							icon={<Icon name="RefreshCcw" />}
						/>
					</div>
					<Button
						variant="primary"
						isLoading={methods.formState.isSubmitting}
						size="sm"
						type="submit"
					>
						{t("general.submit")}
					</Button>
				</div>
			</form>
		</FormProvider>
	);
}
